import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { MatchData, MatchScore, ScoreSubmission, ScoreResponse, ScoreScenario, ScoringRule } from '../../types/tournament';
import { apiClient } from '../../utils/api-client';

// Tipi per il sistema avanzato di scoring
type WinnerDisplayInfo = {
  name: string;
  details?: string | undefined;
};


/**
 * Componente Lit per la gestione del modale di inserimento punteggi
 * Sostituisce score-modal.js con type safety e gestione stato reattiva
 */
@customElement('pm-score-modal')
export class PmScoreModal extends LitElement {
  @property({ type: Object }) currentMatch: MatchData | null = null;
  @property({ type: Boolean }) editMode = false;
  @property({ type: String }) tournamentId = '';

  @state() private isLoading = false;
  @state() private scores: MatchScore = {};
  @state() private errors: Record<string, string> = {};
  @state() private winnerId: number | null = null;
  @state() private winnerDisplayInfo: WinnerDisplayInfo | null = null;
  @state() private scoringRule: ScoringRule = 'classic';
  @state() private showWinnerInfo = false;

  // Bootstrap modal instance
  private modal?: any;
  private backdropElement?: HTMLElement;
  
  // Event listeners cleanup
  private scoreInputListeners: Array<{ element: HTMLElement; handler: EventListener }> = [];

  static styles = css`
    :host {
      display: contents;
    }
    
    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    }
  `;

  firstUpdated() {
    this.initializeModal();
    this.setupGlobalFunctions();
    this.setupFormSubmission();
    this.setupScoreInputListeners();
    this.setupUniversalClickHandler();
    this.detectScoringRule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  /**
   * Inizializza il modale Bootstrap
   */
  private initializeModal() {
    const modalElement = document.getElementById('scoreModal');
    const backdropElement = document.getElementById('scoreModalBackdrop');

    if (modalElement && (window as any).bootstrap) {
      this.modal = new (window as any).bootstrap.Modal(modalElement, {
        backdrop: backdropElement ? false : true,
        keyboard: true
      });

      // Event listeners per gestione backdrop personalizzato
      if (backdropElement) {
        this.backdropElement = backdropElement;
        
        modalElement.addEventListener('show.bs.modal', () => {
          this.showCustomBackdrop();
        });
        
        modalElement.addEventListener('hide.bs.modal', () => {
          this.hideCustomBackdrop();
        });
        
        backdropElement.addEventListener('click', () => {
          this.modal?.hide();
        });
      }
    }
  }

  /**
   * Configura tutte le funzioni globali per compatibilità completa
   */
  private setupGlobalFunctions() {
    // Funzioni principali
    (window as any).openScoreModal = (button: HTMLElement) => {
      this.openFromButton(button);
    };
    
    (window as any).generateRandomScores = () => {
      this.generateRandomScores();
    };
    
    (window as any).clearScores = () => {
      this.clearScores();
    };

    // Funzioni legacy per compatibilità DOM
    (window as any).calculateWinner = () => {
      this.calculateWinner();
    };

    (window as any).hideWinnerInfo = () => {
      this.hideWinnerInfo();
    };

    (window as any).showError = (message: string) => {
      this.showError(message);
    };

    (window as any).hideError = () => {
      this.hideError();
    };

    // Variabile globale per compatibilità
    (window as any).currentMatchId = null;
  }

  /**
   * Configura gli event listeners per il calcolo real-time del vincitore
   */
  private setupScoreInputListeners() {
    const scoreInputs = ['side1_set1', 'side2_set1', 'side1_set2', 'side2_set2', 'side1_set3', 'side2_set3'];
    
    scoreInputs.forEach(inputId => {
      const element = document.getElementById(inputId) as HTMLInputElement;
      if (element) {
        const handler = (e: Event) => {
          this.handleScoreChange(e);
        };
        
        element.addEventListener('input', handler);
        this.scoreInputListeners.push({ element, handler });
      }
    });
  }

  /**
   * Configura l'event delegation universale per l'apertura del modale
   */
  private setupUniversalClickHandler() {
    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.matches('[data-bs-target="#scoreModal"]') || target.closest('[data-bs-target="#scoreModal"]')) {
        const button = target.matches('[data-bs-target="#scoreModal"]') ? target : target.closest('[data-bs-target="#scoreModal"]') as HTMLElement;
        if (button) {
          this.openFromButton(button);
        }
      }
    });
  }

  /**
   * Rileva automaticamente la scoring rule dal DOM
   */
  private detectScoringRule() {
    const scoringRuleElement = document.querySelector('[data-scoring-rule]') as HTMLElement;
    this.scoringRule = (scoringRuleElement?.getAttribute('data-scoring-rule') as ScoringRule) || 'classic';
  }

  /**
   * Cleanup degli event listeners
   */
  private cleanup() {
    this.scoreInputListeners.forEach(({ element, handler }) => {
      element.removeEventListener('input', handler);
    });
    this.scoreInputListeners = [];
  }

  /**
   * Configura la gestione del form submit
   */
  private setupFormSubmission() {
    const scoreForm = document.getElementById('scoreForm') as HTMLFormElement;
    
    if (scoreForm) {
      // Rimuovi eventuali listener precedenti
      scoreForm.removeEventListener('submit', this.handleFormSubmit);
      
      // Aggiungi il nuovo listener
      scoreForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleFormSubmit(e);
      });
    }
  }

  /**
   * Gestisce il submit del form score
   */
  private async handleFormSubmit(event: Event) {
    event.preventDefault();
    
    // PRIMA normalizza i campi vuoti a '0'
    this.normalizeEmptyScoreInputs();
    
    // POI crea il FormData con i valori normalizzati
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const saveBtn = document.getElementById('saveScoreBtn') as HTMLButtonElement;
    
    if (!this.currentMatch) {
      this.showToast('Errore: dati match non disponibili', 'error');
      return;
    }

    if (this.isLoading) {
      return;
    }

    // UI loading state
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Salvando...';
    }

    this.isLoading = true;

    try {
      // Rileva se siamo in modalità simulazione o normale
      const url = this.getScoreSubmissionUrl();
      
      const response = await apiClient.post(url, formData);

      if (response.success) {
        this.showToast(response.message || 'Punteggi salvati con successo', 'success');
        
        // Emette evento per aggiornamento real-time
        this.emitMatchUpdatedEvent(response);
        
        // Chiudi il modale
        this.closeModal();
      } else {
        this.errors = response.errors || {};
        const firstError = Object.values(this.errors)[0];
        const errorMessage = response.message
          || response.error
          || firstError
          || 'Errore durante il salvataggio dei punteggi';
        this.showError(errorMessage);
      }
    } catch (error) {
      console.error('Score submission error:', error);
      this.showError('Errore di connessione durante il salvataggio');
    } finally {
      this.isLoading = false;
      
      // Ripristina il pulsante
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.innerHTML = '<i class="fas fa-save me-1"></i>Salva Punteggi';
      }
    }
  }

  /**
   * Determina l'URL corretto per l'invio dei punteggi
   */
  private getScoreSubmissionUrl(): string {
    if (!this.currentMatch) {
      throw new Error('Match data not available');
    }

    // Rileva se siamo in modalità simulazione dall'URL corrente
    const isSimulation = window.location.pathname.includes('/simulations/');
    
    if (isSimulation) {
      return `/simulations/tournaments/match/${this.currentMatch.id}/score/`;
    } else {
      // Per i tornei normali, abbiamo bisogno del tournament ID
      // Lo estraiamo dall'URL o dal tournamentId property
      const tournamentId = this.tournamentId || this.extractTournamentIdFromUrl();
      return `/tournament/${tournamentId}/match/${this.currentMatch.id}/submit-score/`;
    }
  }

  /**
   * Estrae il tournament ID dall'URL corrente
   */
  private extractTournamentIdFromUrl(): string {
    const pathMatch = window.location.pathname.match(/\/tournament\/(\d+)\//);
    if (pathMatch) {
      return pathMatch[1];
    }
    
    // Fallback: tenta di estrarre da altri elementi della pagina
    const tournamentElement = document.querySelector('[data-tournament-id]') as HTMLElement;
    if (tournamentElement) {
      return tournamentElement.dataset.tournamentId || '';
    }
    
    throw new Error('Cannot determine tournament ID');
  }


  /**
   * Apre il modale da un pulsante HTML (compatibilità)
   */
  openFromButton(button: HTMLElement) {
    const matchData = this.extractMatchDataFromButton(button);
    const editMode = button.getAttribute('data-edit-mode') === 'true';
    
    // Imposta la variabile globale per compatibilità con il legacy
    (window as any).currentMatchId = matchData.id;
    
    // Se è in modalità modifica, precarica i punteggi dal pulsante
    if (editMode) {
      this.preloadScoresFromButton(button);
    }
    
    this.openModal(matchData, editMode);
  }

  /**
   * Estrae i dati del match dal pulsante
   */
  private extractMatchDataFromButton(button: HTMLElement): MatchData {
    const matchId = parseInt(button.getAttribute('data-match-id') || '0');
    const matchNumber = button.getAttribute('data-match-number') || '';
    const editMode = button.getAttribute('data-edit-mode') === 'true';

    // Convenzioni multiple per i nomi dei team
    let side1Name = '', side2Name = '';
    
    // Convenzione 1: player names separati
    const side1Player1 = button.getAttribute('data-side1-player1');
    const side1Player2 = button.getAttribute('data-side1-player2');
    const side2Player1 = button.getAttribute('data-side2-player1');
    const side2Player2 = button.getAttribute('data-side2-player2');
    
    // Convenzione 2: team names diretti
    const side1NameDirect = button.getAttribute('data-side1-name');
    const side2NameDirect = button.getAttribute('data-side2-name');

    if (side1Player1 && side1Player2) {
      side1Name = `${side1Player1} / ${side1Player2}`;
      side2Name = `${side2Player1} / ${side2Player2}`;
    } else {
      side1Name = side1NameDirect || 'Coppia 1';
      side2Name = side2NameDirect || 'Coppia 2';
    }

    const matchData: MatchData = {
      id: matchId,
      match_number: matchNumber,
      side1: { 
        player1: { name: side1Player1 || side1Name }
      },
      side2: { 
        player1: { name: side2Player1 || side2Name }
      },
      status: 'scheduled'
    };

    if (side1Player2) {
      matchData.side1.player2 = { name: side1Player2 };
    }

    if (side2Player2) {
      matchData.side2.player2 = { name: side2Player2 };
    }

    return matchData;
  }

  /**
   * Precarica i punteggi esistenti dal pulsante in modalità edit
   */
  private preloadScoresFromButton(button: HTMLElement) {
    const scores: MatchScore = {};
    
    const scoreKeys = ['side1_set1', 'side2_set1', 'side1_set2', 'side2_set2', 'side1_set3', 'side2_set3'] as const;
    
    scoreKeys.forEach(key => {
      const attrValue = button.getAttribute(`data-${key.replace('_', '-')}`);
      if (attrValue && attrValue !== '0') {
        const numValue = parseInt(attrValue);
        if (!isNaN(numValue)) {
          scores[key] = numValue;
        }
      }
    });

    this.scores = scores;
    
    // Precarica i valori negli input DOM
    this.updateDOMInputsFromScores();
  }

  /**
   * Aggiorna gli input DOM con i valori dei punteggi
   */
  private updateDOMInputsFromScores() {
    Object.entries(this.scores).forEach(([key, value]) => {
      const element = document.getElementById(key) as HTMLInputElement;
      if (element && value !== undefined) {
        element.value = value.toString();
      }
    });
  }

  /**
   * Apre il modale con i dati del match
   */
  openModal(match: MatchData, editMode: boolean = false) {
    this.currentMatch = match;
    this.editMode = editMode;
    
    if (!editMode) {
      this.scores = {};
      this.clearScores();
    }
    
    this.winnerId = match.winner_id || null;
    this.errors = {};
    this.showWinnerInfo = false;
    this.winnerDisplayInfo = null;

    // Aggiorna le informazioni nel modale
    this.updateModalContent(match, editMode);

    // Aggiorna l'input hidden con l'ID del match
    const matchIdInput = document.getElementById('matchId') as HTMLInputElement;
    if (matchIdInput) {
      matchIdInput.value = match.id.toString();
    }

    // Calcola il vincitore se ci sono punteggi
    if (editMode || Object.keys(this.scores).length > 0) {
      this.calculateWinner();
    }

    this.hideError();

    if (this.modal) {
      this.modal.show();
    }
  }

  /**
   * Aggiorna il contenuto del modale (titoli, nomi coppie, etc.)
   */
  private updateModalContent(match: MatchData, editMode: boolean) {
    // Aggiorna info match
    const matchInfo = document.getElementById('matchInfo');
    if (matchInfo) {
      matchInfo.textContent = `Match ${match.match_number}`;
    }

    // Aggiorna nomi coppie
    const side1Name = document.getElementById('side1Name');
    const side2Name = document.getElementById('side2Name');
    
    if (side1Name && side2Name) {
      side1Name.textContent = this.getPairDisplayName(match.side1);
      side2Name.textContent = this.getPairDisplayName(match.side2);
    }

    // Aggiorna titolo modale
    const modalTitle = document.getElementById('scoreModalLabel');
    const saveBtn = document.getElementById('saveScoreBtn');
    
    if (modalTitle && saveBtn) {
      if (editMode) {
        modalTitle.innerHTML = '<i class="fas fa-edit me-2"></i>Modifica Punteggi Match';
        saveBtn.innerHTML = '<i class="fas fa-save me-1"></i>Aggiorna Punteggi';
      } else {
        modalTitle.innerHTML = '<i class="fas fa-edit me-2"></i>Inserisci Punteggi Match';
        saveBtn.innerHTML = '<i class="fas fa-save me-1"></i>Salva Punteggi';
      }
    }
  }

  /**
   * Ottiene il nome display di una coppia
   */
  private getPairDisplayName(team: any): string {
    if (team.player2) {
      return `${team.player1.name} / ${team.player2.name}`;
    }
    return team.player1.name;
  }

  /**
   * Chiude il modale
   */
  closeModal() {
    if (this.modal) {
      this.modal.hide();
    }
  }

  /**
   * Mostra backdrop personalizzato
   */
  private showCustomBackdrop() {
    if (this.backdropElement) {
      this.backdropElement.style.display = 'block';
      this.backdropElement.offsetHeight; // Force reflow
      this.backdropElement.classList.add('show');
    }
  }

  /**
   * Nasconde backdrop personalizzato
   */
  private hideCustomBackdrop() {
    if (this.backdropElement) {
      this.backdropElement.classList.remove('show');
      setTimeout(() => {
        if (this.backdropElement) {
          this.backdropElement.style.display = 'none';
        }
      }, 150);
    }
  }

  /**
   * Gestisce il cambio di punteggio
   */
  private handleScoreChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value) || 0;
    
    this.scores = {
      ...this.scores,
      [input.name]: value
    };

    // Calcola automaticamente il vincitore
    this.calculateWinner();
    this.requestUpdate();
  }

  /**
   * Calcola il vincitore automaticamente (supporta classic e free_games)
   */
  private calculateWinner() {
    // Prima aggiorna i punteggi dal DOM
    this.updateScoresFromInputs();
    
    const { side1_set1 = 0, side1_set2 = 0, side1_set3 = 0, 
            side2_set1 = 0, side2_set2 = 0, side2_set3 = 0 } = this.scores;

    let winnerDetermined = false;
    let winnerName = '';
    let winnerDetails = '';

    if (this.scoringRule === 'free_games') {
      // Sistema free games: vince chi ha più game totali, pareggi ammessi
      const side1TotalGames = side1_set1 + side1_set2 + side1_set3;
      const side2TotalGames = side2_set1 + side2_set2 + side2_set3;

      // Verifica che ci sia almeno un game giocato
      if (side1TotalGames > 0 || side2TotalGames > 0) {
        winnerDetermined = true;
        
        if (side1TotalGames > side2TotalGames) {
          winnerName = this.getSide1Name();
          this.winnerId = 1;
          winnerDetails = `Game totali: ${side1TotalGames} - ${side2TotalGames}`;
        } else if (side2TotalGames > side1TotalGames) {
          winnerName = this.getSide2Name();
          this.winnerId = 2;
          winnerDetails = `Game totali: ${side1TotalGames} - ${side2TotalGames}`;
        } else {
          // Pareggio
          winnerName = 'Pareggio';
          this.winnerId = null;
          winnerDetails = `Game totali: ${side1TotalGames} - ${side2TotalGames}`;
        }
      } else {
        this.winnerId = null;
      }
    } else {
      // Sistema classico basato sui set
      let side1Sets = 0;
      let side2Sets = 0;

      // Conta i set vinti
      if (side1_set1 > side2_set1) side1Sets++;
      else if (side2_set1 > side1_set1) side2Sets++;

      if (side1_set2 > side2_set2) side1Sets++;
      else if (side2_set2 > side1_set2) side2Sets++;

      if (side1_set3 > 0 || side2_set3 > 0) {
        if (side1_set3 > side2_set3) side1Sets++;
        else if (side2_set3 > side1_set3) side2Sets++;
      }

      // Mostra il vincitore se qualcuno ha vinto almeno 2 set
      if (side1Sets >= 2 || side2Sets >= 2) {
        winnerDetermined = true;
        
        if (side1Sets > side2Sets) {
          winnerName = this.getSide1Name();
          this.winnerId = 1;
        } else {
          winnerName = this.getSide2Name();
          this.winnerId = 2;
        }
      } else {
        this.winnerId = null;
      }
    }

    // Aggiorna stato del display vincitore
    if (winnerDetermined) {
      const displayInfo: WinnerDisplayInfo = {
        name: winnerName
      };
      
      if (winnerDetails) {
        displayInfo.details = winnerDetails;
      }
      
      this.winnerDisplayInfo = displayInfo;
      this.showWinnerInfo = true;
      this.updateWinnerDisplay();
    } else {
      this.winnerDisplayInfo = null;
      this.showWinnerInfo = false;
      this.hideWinnerInfo();
    }
  }

  /**
   * Ottiene il nome del team 1
   */
  private getSide1Name(): string {
    const element = document.getElementById('side1Name');
    return element?.textContent || 'Coppia 1';
  }

  /**
   * Ottiene il nome del team 2
   */
  private getSide2Name(): string {
    const element = document.getElementById('side2Name');
    return element?.textContent || 'Coppia 2';
  }

  /**
   * Aggiorna il display del vincitore nel DOM
   */
  private updateWinnerDisplay() {
    const calculatedWinner = document.getElementById('calculatedWinner');
    const winnerInfo = document.getElementById('winnerInfo');
    
    if (calculatedWinner && this.winnerDisplayInfo) {
      if (this.scoringRule === 'free_games' && this.winnerDisplayInfo.details) {
        calculatedWinner.innerHTML = 
          `${this.winnerDisplayInfo.name}<br><small class="text-muted">${this.winnerDisplayInfo.details}</small>`;
      } else {
        calculatedWinner.textContent = this.winnerDisplayInfo.name;
      }
    }
    
    if (winnerInfo) {
      winnerInfo.style.display = 'block';
    }
  }

  /**
   * Nasconde le informazioni del vincitore
   */
  private hideWinnerInfo() {
    const winnerInfo = document.getElementById('winnerInfo');
    if (winnerInfo) {
      winnerInfo.style.display = 'none';
    }
    this.showWinnerInfo = false;
  }

  /**
   * Invia i punteggi al server
   */
  private async submitScore(action: 'submit' | 'confirm' | 'dispute' = 'submit') {
    if (!this.currentMatch) return;

    if (this.isLoading) {
      return;
    }

    const submission: ScoreSubmission = {
      match_id: this.currentMatch.id,
      side1_set1: this.scores.side1_set1 || 0,
      side1_set2: this.scores.side1_set2 || 0,
      side1_set3: this.scores.side1_set3 || 0,
      side2_set1: this.scores.side2_set1 || 0,
      side2_set2: this.scores.side2_set2 || 0,
      side2_set3: this.scores.side2_set3 || 0,
      winner_id: this.winnerId,
      action
    };

    this.isLoading = true;
    this.errors = {};

    try {
      const response = await apiClient.post<ScoreResponse>(`/match/${this.currentMatch.id}/submit-score/`, submission);

      if (response.success) {
        this.showToast(response.message || 'Punteggio salvato con successo', 'success');
        
        // Emette evento per aggiornamento real-time
        this.emitMatchUpdatedEvent(response);
        
        this.closeModal();
      } else {
        this.errors = response.errors || {};
        this.showToast(response.message || 'Errore nel salvare i punteggi', 'error');
      }
    } catch (error) {
      console.error('Score submission error:', error);
      this.showToast('Errore di connessione. Riprova.', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Genera punteggi casuali per testing/simulazione
   */
  private generateRandomScores() {
    // Rileva il tipo di scoring rule
    const scoringRuleElement = document.querySelector('[data-scoring-rule]') as HTMLElement;
    const scoringRule = scoringRuleElement?.getAttribute('data-scoring-rule') || 'classic';

    if (scoringRule === 'free_games') {
      this.generateFreeGameScores();
    } else {
      this.generateClassicScores();
    }

    // Aggiorna i punteggi nel componente
    this.updateScoresFromInputs();
    this.calculateWinner();
  }

  /**
   * Genera punteggi per mini-tornei free games
   */
  private generateFreeGameScores() {
    const freeGameScenarios: Array<{ set1: [number, number], set2: [number, number], set3: [number, number] }> = [
      { set1: [6, 3], set2: [0, 0], set3: [0, 0] },
      { set1: [8, 2], set2: [0, 0], set3: [0, 0] },
      { set1: [5, 1], set2: [0, 0], set3: [0, 0] },
      { set1: [6, 4], set2: [3, 7], set3: [0, 0] },
      { set1: [4, 6], set2: [8, 2], set3: [0, 0] },
      { set1: [7, 3], set2: [2, 6], set3: [0, 0] },
      { set1: [6, 4], set2: [3, 6], set3: [5, 2] },
    ];

    const scenario = freeGameScenarios[Math.floor(Math.random() * freeGameScenarios.length)];
    const side1Wins = Math.random() < 0.5;

    if (side1Wins) {
      this.setScoreInputs(scenario.set1[0], scenario.set1[1], scenario.set2[0], scenario.set2[1], scenario.set3[0], scenario.set3[1]);
    } else {
      this.setScoreInputs(scenario.set1[1], scenario.set1[0], scenario.set2[1], scenario.set2[0], scenario.set3[1], scenario.set3[0]);
    }

    // Verifica che non ci sia pareggio nei game totali (logica avanzata)
    this.adjustForTieBreaker();
    
    // Aggiorna i punteggi nel componente e calcola vincitore
    this.updateScoresFromInputs();
    this.calculateWinner();
  }

  /**
   * Genera punteggi per sistema classico
   */
  private generateClassicScores() {
    const scenarios: Array<{ set1: [number, number], set2: [number, number], set3: [number | null, number | null] }> = [
      // Vittoria 2-0
      { set1: [6, 3], set2: [6, 4], set3: [null, null] },
      { set1: [6, 2], set2: [6, 1], set3: [null, null] },
      { set1: [6, 4], set2: [7, 5], set3: [null, null] },
      { set1: [7, 6], set2: [6, 3], set3: [null, null] },
      
      // Vittoria 2-1
      { set1: [6, 4], set2: [3, 6], set3: [6, 2] },
      { set1: [7, 6], set2: [4, 6], set3: [6, 4] },
      { set1: [6, 2], set2: [5, 7], set3: [6, 3] },
      { set1: [4, 6], set2: [6, 1], set3: [6, 4] },
    ];

    const scenario = scenarios[Math.floor(Math.random() * scenarios.length)];
    const side1Wins = Math.random() < 0.5;

    if (side1Wins) {
      this.setScoreInputs(
        scenario.set1[0], scenario.set1[1],
        scenario.set2[0], scenario.set2[1],
        scenario.set3[0], scenario.set3[1]
      );
    } else {
      this.setScoreInputs(
        scenario.set1[1], scenario.set1[0],
        scenario.set2[1], scenario.set2[0],
        scenario.set3[1], scenario.set3[0]
      );
    }

    // Aggiorna i punteggi nel componente e calcola vincitore
    this.updateScoresFromInputs();
    this.calculateWinner();
  }

  /**
   * Imposta i valori negli input del DOM
   */
  private setScoreInputs(
    side1Set1: number | null, side2Set1: number | null,
    side1Set2: number | null, side2Set2: number | null,
    side1Set3: number | null, side2Set3: number | null
  ) {
    const setInput = (id: string, value: number | null) => {
      const element = document.getElementById(id) as HTMLInputElement;
      if (element) {
        element.value = value?.toString() || '';
      }
    };

    setInput('side1_set1', side1Set1);
    setInput('side2_set1', side2Set1);
    setInput('side1_set2', side1Set2);
    setInput('side2_set2', side2Set2);
    setInput('side1_set3', side1Set3);
    setInput('side2_set3', side2Set3);
  }

  /**
   * Aggiusta i punteggi per evitare pareggi (logica avanzata come nel legacy)
   */
  private adjustForTieBreaker() {
    const getInputValue = (id: string): number => {
      const element = document.getElementById(id) as HTMLInputElement;
      return parseInt(element?.value || '0') || 0;
    };

    const total1 = getInputValue('side1_set1') + getInputValue('side1_set2') + getInputValue('side1_set3');
    const total2 = getInputValue('side2_set1') + getInputValue('side2_set2') + getInputValue('side2_set3');

    // Se c'è pareggio, aggiungi un game a una coppia casuale
    if (total1 === total2 && total1 > 0) {
      const side1Element = document.getElementById('side1_set1') as HTMLInputElement;
      const side2Element = document.getElementById('side2_set1') as HTMLInputElement;
      
      if (Math.random() < 0.5 && side1Element) {
        const currentValue = parseInt(side1Element.value || '0') || 0;
        side1Element.value = (currentValue + 1).toString();
      } else if (side2Element) {
        const currentValue = parseInt(side2Element.value || '0') || 0;
        side2Element.value = (currentValue + 1).toString();
      }
    }
  }

  /**
   * Aggiorna i punteggi del componente dai valori degli input DOM
   */
  private updateScoresFromInputs() {
    const getInputValue = (id: string): number => {
      const element = document.getElementById(id) as HTMLInputElement;
      return parseInt(element?.value || '0') || 0;
    };

    this.scores = {
      side1_set1: getInputValue('side1_set1'),
      side1_set2: getInputValue('side1_set2'),
      side1_set3: getInputValue('side1_set3'),
      side2_set1: getInputValue('side2_set1'),
      side2_set2: getInputValue('side2_set2'),
      side2_set3: getInputValue('side2_set3'),
    };
  }

  /**
   * Normalizza gli input vuoti dei punteggi prima del submit
   * Converte TUTTI i campi vuoti in '0' per evitare errori di parsing
   * Il backend si occuperà della validazione (es: almeno un game giocato)
   */
  private normalizeEmptyScoreInputs() {
    const scoreInputIds = ['side1_set1', 'side2_set1', 'side1_set2', 'side2_set2', 'side1_set3', 'side2_set3'];
    
    scoreInputIds.forEach(inputId => {
      const element = document.getElementById(inputId) as HTMLInputElement;
      if (element && (element.value === '' || element.value === null || element.value === undefined)) {
        element.value = '0';
      }
    });
  }

  /**
   * Mostra errore usando il sistema toast moderno
   */
  private showError(message: string) {
    // Nascondi l'alert legacy nel modale se presente
    this.hideError();
    
    // Usa solo il toast per messaggi puliti e non duplicati
    this.showToast(message, 'error');
  }

  /**
   * Nasconde errore dal DOM legacy
   */
  private hideError() {
    const errorAlert = document.getElementById('errorMessage');
    if (errorAlert) {
      errorAlert.style.display = 'none';
    }
  }

  /**
   * Pulisce tutti i punteggi
   */
  private clearScores() {
    const inputs = ['side1_set1', 'side2_set1', 'side1_set2', 'side2_set2', 'side1_set3', 'side2_set3'];
    
    inputs.forEach(id => {
      const element = document.getElementById(id) as HTMLInputElement;
      if (element) {
        element.value = '';
      }
    });

    // Reset stato del componente
    this.scores = {};
    this.winnerId = null;
    this.errors = {};
    this.winnerDisplayInfo = null;
    this.showWinnerInfo = false;

    // Nascondi informazioni vincitore/risultato (compatibilità con codice legacy)
    this.hideWinnerInfo();
    this.hideError();
    this.hideLegacyElements();
  }

  /**
   * Nasconde elementi legacy per compatibilità
   */
  private hideLegacyElements() {
    const hideElement = (selector: string) => {
      const element = document.querySelector(selector);
      if (element) {
        (element as HTMLElement).style.display = 'none';
      }
    };

    // Compatibilità con il codice JavaScript legacy
    hideElement('#winnerInfo');
    hideElement('#currentResult');
    hideElement('#errorMessage');
  }

  /**
   * Emette evento per aggiornamento real-time dei match cards
   */
  private emitMatchUpdatedEvent(response: any) {
    if (!this.currentMatch) return;

    const matchUpdatedEvent = new CustomEvent('match-updated', {
      detail: {
        matchId: this.currentMatch.id,
        status: 'completed',
        winnerScore: this.formatScoreForDisplay(),
        side1IsWinner: this.winnerId === 1,
        side2IsWinner: this.winnerId === 2,
        isDraw: this.winnerId === null,  // Flag per pareggi (Free Games)
        rawResponse: response
      },
      bubbles: true,
      composed: true
    });

    // Emette l'evento dal document per raggiungere tutti i componenti
    document.dispatchEvent(matchUpdatedEvent);
  }

  /**
   * Formatta il punteggio per la visualizzazione nelle card
   * I punteggi sono sempre mostrati dal punto di vista del match (side1 vs side2)
   * indipendentemente da chi vince
   */
  private formatScoreForDisplay(): string {
    if (!this.scores) return '';

    const { side1_set1 = 0, side1_set2 = 0, side1_set3 = 0,
            side2_set1 = 0, side2_set2 = 0, side2_set3 = 0 } = this.scores;

    const sets: string[] = [];
    
    // Set 1 - sempre side1-side2 (format consistente)
    if (side1_set1 > 0 || side2_set1 > 0) {
      sets.push(`${side1_set1}-${side2_set1}`);
    }
    
    // Set 2 - sempre side1-side2 (format consistente)
    if (side1_set2 > 0 || side2_set2 > 0) {
      sets.push(`${side1_set2}-${side2_set2}`);
    }
    
    // Set 3 - sempre side1-side2 (format consistente)
    if (side1_set3 > 0 || side2_set3 > 0) {
      sets.push(`${side1_set3}-${side2_set3}`);
    }

    return sets.join(', ');
  }

  /**
   * Mostra toast usando il sistema globale
   */
  private showToast(message: string, variant: 'success' | 'error' | 'warning' | 'info') {
    const windowAny = window as any;
    
    // PRIORITÀ: usa sempre PadelMatcher.ui.showToast se disponibile (sistema Lit ufficiale)
    const globalShowToast = windowAny?.PadelMatcher?.ui?.showToast ?? windowAny.showToast;
    
    if (globalShowToast) {
      globalShowToast({
        message,
        variant: variant === 'error' ? 'danger' : variant,
        autohide: variant === 'success' ? 3000 : 5000
      });
    } else {
      // Fallback: usa direttamente il componente pm-toast se disponibile
      const toastElement = document.querySelector('pm-toast#pm-global-toast') as any;
      
      if (toastElement && typeof toastElement.show === 'function') {
        toastElement.show({
          message,
          variant: variant === 'error' ? 'danger' : variant,
          autohide: variant === 'success' ? 3000 : 5000
        });
      }
    }
  }

  render() {
    // Questo componente gestisce solo la logica
    // Il template HTML rimane nel DOM esistente
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-score-modal': PmScoreModal;
  }
}